import { FetchResult } from '@apollo/client/link/core'

import { BillingAddressInput, CartAddressInput, CartFragment, Maybe } from '../../graphql/magento'
import { checkAndDiscardQueryResponse } from '../../utils/graphqlTools'
import {
  areAddressFieldValuesEqual,
  fieldValuesFromCartAddress,
  fieldValuesFromCustomerAddress,
  shouldSaveInAddressBook,
} from './address'
import { CartUpdateError } from './errors'
import {
  CartBillingAddress,
  CartShippingAddress,
  CustomerAddressFromQuery,
  ValidFormValues,
} from './types'

export const shouldUpdateBillingAddress = ({
  cart,
  addressValues,
}: {
  cart: CartFragment
  addressValues: ValidFormValues.Address
}): boolean => {
  const { billing_address: cartBillingAddress } = cart
  return (
    !cartBillingAddress ||
    !areAddressFieldValuesEqual(fieldValuesFromCartAddress(cartBillingAddress), addressValues)
  )
}

export const updateBillingAddressIfNeeded =
  ({
    addressInput,
    addressValues,
    customerAddresses,
    customerCart,
    updateBillingAddress,
  }: {
    addressInput: CartAddressInput
    addressValues: ValidFormValues.Address
    customerAddresses?: Maybe<CustomerAddressFromQuery[]>
    customerCart: CartFragment
    updateBillingAddress: (address: BillingAddressInput['address']) => Promise<FetchResult>
  }) =>
  (): Promise<void> =>
    shouldUpdateBillingAddress({ cart: customerCart, addressValues })
      ? updateBillingAddress({
          ...addressInput,
          save_in_address_book: shouldSaveInAddressBook({
            customerAddresses,
            fieldValues: addressValues,
          }),
        })
          .then(checkAndDiscardQueryResponse)
          .catch((err) => {
            throw new CartUpdateError('updateBillingAddress', err)
          })
      : Promise.resolve()

export const defaultBillingAddressValues = ({
  billingAddress,
  billingSameAsShipping,
  defaultBillingAddress,
  defaultCountryCode,
  shippingAddress,
}: {
  billingAddress: CartBillingAddress | undefined
  billingSameAsShipping: boolean
  defaultBillingAddress: CustomerAddressFromQuery | undefined
  defaultCountryCode: string
  shippingAddress: CartShippingAddress | undefined
}): ValidFormValues.Address =>
  billingSameAsShipping
    ? fieldValuesFromCartAddress(shippingAddress)
    : fieldValuesFromCartAddress(
        billingAddress,
        defaultBillingAddress
          ? fieldValuesFromCustomerAddress(defaultBillingAddress)
          : { country_code: defaultCountryCode },
      )
